import React, { useState, createElement } from 'react';
import { SearchIcon, FilterIcon, XIcon, ChevronRightIcon, ArrowUpIcon, ArrowDownIcon, DownloadIcon, PaperclipIcon, TagIcon } from 'lucide-react';
import { Note } from './NotesManagement';
import { StatusBadge } from '../../StatusBadge';
type SortField = 'timestamp' | 'module' | 'noteType' | 'author' | 'visibility';
type SortDirection = 'asc' | 'desc';
interface NotesTableProps {
  notes: Note[];
  onViewNote: (note: Note) => void;
  locale: string;
}
export function NotesTable({
  notes,
  onViewNote,
  locale
}: NotesTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    moduleType: '',
    noteType: '',
    author: '',
    visibility: '',
    entity: '',
    site: '',
    dateFrom: '',
    dateTo: ''
  });
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  const resetFilters = () => {
    setFilters({
      moduleType: '',
      noteType: '',
      author: '',
      visibility: '',
      entity: '',
      site: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchTerm('');
  };
  // Get unique values for filter dropdowns
  const uniqueModuleTypes = Array.from(new Set(notes.map(note => note.moduleType)));
  const uniqueNoteTypes = Array.from(new Set(notes.map(note => note.noteTypeName)));
  const uniqueAuthors = Array.from(new Set(notes.map(note => note.authorName)));
  const uniqueVisibilities = Array.from(new Set(notes.map(note => note.visibility)));
  const uniqueEntities = Array.from(new Set(notes.filter(note => note.entityName).map(note => note.entityName)));
  const uniqueSites = Array.from(new Set(notes.filter(note => note.siteName).map(note => note.siteName)));
  // Apply filters and search
  const filteredNotes = notes.filter(note => {
    // Search term filter
    const searchMatch = searchTerm === '' || note.body.toLowerCase().includes(searchTerm.toLowerCase()) || note.tags && note.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) || note.moduleId.toLowerCase().includes(searchTerm.toLowerCase()) || note.moduleName.toLowerCase().includes(searchTerm.toLowerCase());
    // Module type filter
    const moduleTypeMatch = !filters.moduleType || note.moduleType === filters.moduleType;
    // Note type filter
    const noteTypeMatch = !filters.noteType || note.noteTypeName === filters.noteType;
    // Author filter
    const authorMatch = !filters.author || note.authorName === filters.author;
    // Visibility filter
    const visibilityMatch = !filters.visibility || note.visibility === filters.visibility;
    // Entity filter
    const entityMatch = !filters.entity || note.entityName === filters.entity;
    // Site filter
    const siteMatch = !filters.site || note.siteName === filters.site;
    // Date range filter
    const noteDate = new Date(note.createdAt);
    const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const toDate = filters.dateTo ? new Date(filters.dateTo) : null;
    const dateMatch = (!fromDate || noteDate >= fromDate) && (!toDate || noteDate <= toDate);
    return searchMatch && moduleTypeMatch && noteTypeMatch && authorMatch && visibilityMatch && entityMatch && siteMatch && dateMatch;
  });
  // Apply sorting
  const sortedNotes = [...filteredNotes].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'timestamp':
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      case 'module':
        const moduleCompare = a.moduleType.localeCompare(b.moduleType);
        if (moduleCompare !== 0) {
          comparison = moduleCompare;
        } else {
          comparison = a.moduleId.localeCompare(b.moduleId);
        }
        break;
      case 'noteType':
        comparison = a.noteTypeName.localeCompare(b.noteTypeName);
        break;
      case 'author':
        comparison = a.authorName.localeCompare(b.authorName);
        break;
      case 'visibility':
        comparison = a.visibility.localeCompare(b.visibility);
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Function to export notes to CSV
  const exportToCSV = () => {
    // Header row
    let csvContent = 'Module Type,Module ID,Module Name,Note Type,Visibility,Author,Created At,Body,Tags\n';
    // Data rows
    sortedNotes.forEach(note => {
      const row = [note.moduleType, note.moduleId, note.moduleName, note.noteTypeName, note.visibility, note.authorName, new Date(note.createdAt).toLocaleString(locale), `"${note.body.replace(/"/g, '""')}"`, note.tags ? note.tags.join(', ') : ''];
      csvContent += row.join(',') + '\n';
    });
    // Create and download the file
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `notes_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Truncate body text for display
  const truncateBody = (body: string, maxLength = 100) => {
    if (body.length <= maxLength) return body;
    return body.substring(0, maxLength) + '...';
  };
  // Render visibility badge
  const renderVisibilityBadge = (visibility: string) => {
    switch (visibility) {
      case 'Public':
        return <StatusBadge status="success" text="Public" data-id="element-18865" />;
      case 'Role-based':
        return <StatusBadge status="warning" text="Role-based" data-id="element-18866" />;
      case 'User-specific':
        return <StatusBadge status="info" text="User-specific" data-id="element-18867" />;
      case 'Creator-only':
        return <StatusBadge status="error" text="Private" data-id="element-18868" />;
      default:
        return <StatusBadge status="default" text={visibility} data-id="element-18869" />;
    }
  };
  return <div className="space-y-6" data-id="element-18870">
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-18871">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-18872">
          <div className="flex-1 max-w-md" data-id="element-18873">
            <div className="relative rounded-md shadow-sm" data-id="element-18874">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-18875">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-18876" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search in note body or tags..." data-id="element-18877" />
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-18878">
            <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18879">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-18880" />
              Filters
              {Object.values(filters).some(f => f !== '') && <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-18881">
                  {Object.values(filters).filter(f => f !== '').length}
                </span>}
            </button>
            <button onClick={exportToCSV} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18882">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-18883" />
              Export CSV
            </button>
          </div>
        </div>
        {/* Filter chips */}
        {Object.values(filters).some(f => f !== '') && <div className="mt-4 flex flex-wrap gap-2" data-id="element-18884">
            {filters.moduleType && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-18885">
                Module: {filters.moduleType}
                <button type="button" onClick={() => setFilters({
            ...filters,
            moduleType: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none" data-id="element-18886">
                  <XIcon className="h-3 w-3" data-id="element-18887" />
                </button>
              </span>}
            {filters.noteType && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800" data-id="element-18888">
                Type: {filters.noteType}
                <button type="button" onClick={() => setFilters({
            ...filters,
            noteType: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none" data-id="element-18889">
                  <XIcon className="h-3 w-3" data-id="element-18890" />
                </button>
              </span>}
            {filters.author && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-18891">
                Author: {filters.author}
                <button type="button" onClick={() => setFilters({
            ...filters,
            author: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none" data-id="element-18892">
                  <XIcon className="h-3 w-3" data-id="element-18893" />
                </button>
              </span>}
            {filters.visibility && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800" data-id="element-18894">
                Visibility: {filters.visibility}
                <button type="button" onClick={() => setFilters({
            ...filters,
            visibility: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-yellow-400 hover:bg-yellow-200 hover:text-yellow-500 focus:outline-none" data-id="element-18895">
                  <XIcon className="h-3 w-3" data-id="element-18896" />
                </button>
              </span>}
            {filters.entity && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-red-100 text-red-800" data-id="element-18897">
                Entity: {filters.entity}
                <button type="button" onClick={() => setFilters({
            ...filters,
            entity: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-red-400 hover:bg-red-200 hover:text-red-500 focus:outline-none" data-id="element-18898">
                  <XIcon className="h-3 w-3" data-id="element-18899" />
                </button>
              </span>}
            {filters.site && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800" data-id="element-18900">
                Site: {filters.site}
                <button type="button" onClick={() => setFilters({
            ...filters,
            site: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-500 focus:outline-none" data-id="element-18901">
                  <XIcon className="h-3 w-3" data-id="element-18902" />
                </button>
              </span>}
            {(filters.dateFrom || filters.dateTo) && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-18903">
                Date:{' '}
                {filters.dateFrom ? new Date(filters.dateFrom).toLocaleDateString(locale) : 'Any'}
                {filters.dateTo ? ` to ${new Date(filters.dateTo).toLocaleDateString(locale)}` : ''}
                <button type="button" onClick={() => setFilters({
            ...filters,
            dateFrom: '',
            dateTo: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-500 focus:outline-none" data-id="element-18904">
                  <XIcon className="h-3 w-3" data-id="element-18905" />
                </button>
              </span>}
            <button type="button" onClick={resetFilters} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18906">
              Clear All
            </button>
          </div>}
        {/* Expanded filters */}
        {showFilters && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-3" data-id="element-18907">
            <div data-id="element-18908">
              <label htmlFor="moduleType" className="block text-sm font-medium text-gray-700" data-id="element-18909">
                Module Type
              </label>
              <select id="moduleType" name="moduleType" value={filters.moduleType} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-18910">
                <option value="" data-id="element-18911">All Modules</option>
                {uniqueModuleTypes.map(moduleType => <option key={moduleType} value={moduleType} data-id="element-18912">
                    {moduleType}
                  </option>)}
              </select>
            </div>
            <div data-id="element-18913">
              <label htmlFor="noteType" className="block text-sm font-medium text-gray-700" data-id="element-18914">
                Note Type
              </label>
              <select id="noteType" name="noteType" value={filters.noteType} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-18915">
                <option value="" data-id="element-18916">All Types</option>
                {uniqueNoteTypes.map(noteType => <option key={noteType} value={noteType} data-id="element-18917">
                    {noteType}
                  </option>)}
              </select>
            </div>
            <div data-id="element-18918">
              <label htmlFor="author" className="block text-sm font-medium text-gray-700" data-id="element-18919">
                Author
              </label>
              <select id="author" name="author" value={filters.author} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-18920">
                <option value="" data-id="element-18921">All Authors</option>
                {uniqueAuthors.map(author => <option key={author} value={author} data-id="element-18922">
                    {author}
                  </option>)}
              </select>
            </div>
            <div data-id="element-18923">
              <label htmlFor="visibility" className="block text-sm font-medium text-gray-700" data-id="element-18924">
                Visibility
              </label>
              <select id="visibility" name="visibility" value={filters.visibility} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-18925">
                <option value="" data-id="element-18926">All Visibilities</option>
                {uniqueVisibilities.map(visibility => <option key={visibility} value={visibility} data-id="element-18927">
                    {visibility}
                  </option>)}
              </select>
            </div>
            <div data-id="element-18928">
              <label htmlFor="entity" className="block text-sm font-medium text-gray-700" data-id="element-18929">
                Entity
              </label>
              <select id="entity" name="entity" value={filters.entity} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-18930">
                <option value="" data-id="element-18931">All Entities</option>
                {uniqueEntities.map(entity => <option key={entity} value={entity} data-id="element-18932">
                    {entity}
                  </option>)}
              </select>
            </div>
            <div data-id="element-18933">
              <label htmlFor="site" className="block text-sm font-medium text-gray-700" data-id="element-18934">
                Site
              </label>
              <select id="site" name="site" value={filters.site} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-18935">
                <option value="" data-id="element-18936">All Sites</option>
                {uniqueSites.map(site => <option key={site} value={site} data-id="element-18937">
                    {site}
                  </option>)}
              </select>
            </div>
            <div data-id="element-18938">
              <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700" data-id="element-18939">
                From Date
              </label>
              <input type="date" id="dateFrom" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-18940" />
            </div>
            <div data-id="element-18941">
              <label htmlFor="dateTo" className="block text-sm font-medium text-gray-700" data-id="element-18942">
                To Date
              </label>
              <input type="date" id="dateTo" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-18943" />
            </div>
          </div>}
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-18944">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-18945">
          <thead className="bg-gray-50" data-id="element-18946">
            <tr data-id="element-18947">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('timestamp')} data-id="element-18948">
                <div className="flex items-center space-x-1" data-id="element-18949">
                  <span data-id="element-18950">Timestamp</span>
                  {sortField === 'timestamp' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-18951" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-18952" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('module')} data-id="element-18953">
                <div className="flex items-center space-x-1" data-id="element-18954">
                  <span data-id="element-18955">Module</span>
                  {sortField === 'module' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-18956" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-18957" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('noteType')} data-id="element-18958">
                <div className="flex items-center space-x-1" data-id="element-18959">
                  <span data-id="element-18960">Note Type</span>
                  {sortField === 'noteType' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-18961" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-18962" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('author')} data-id="element-18963">
                <div className="flex items-center space-x-1" data-id="element-18964">
                  <span data-id="element-18965">Author</span>
                  {sortField === 'author' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-18966" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-18967" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('visibility')} data-id="element-18968">
                <div className="flex items-center space-x-1" data-id="element-18969">
                  <span data-id="element-18970">Visibility</span>
                  {sortField === 'visibility' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-18971" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-18972" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18973">
                Content
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18974">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-18975">
            {sortedNotes.length === 0 ? <tr data-id="element-18976">
                <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-18977">
                  No notes found matching the current filters.
                </td>
              </tr> : sortedNotes.map(note => <tr key={note.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onViewNote(note)} data-id="element-18978">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-18979">
                    {formatTimestamp(note.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-18980">
                    <div className="text-sm font-medium text-indigo-600" data-id="element-18981">
                      {note.moduleType} #{note.moduleId}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-18982">
                      {note.moduleName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-18983">
                    {note.noteTypeName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-18984">
                    {note.authorName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-18985">
                    {renderVisibilityBadge(note.visibility)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500" data-id="element-18986">
                    <div className="max-w-xs" data-id="element-18987">
                      <p className="truncate" data-id="element-18988">{truncateBody(note.body)}</p>
                      {note.tags && note.tags.length > 0 && <div className="mt-1 flex flex-wrap gap-1" data-id="element-18989">
                          {note.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-18990">
                              <TagIcon className="h-3 w-3 mr-1" data-id="element-18991" />
                              {tag}
                            </span>)}
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-18992">
                    <div className="flex justify-end items-center" data-id="element-18993">
                      {note.hasAttachment && <PaperclipIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-18994" />}
                      <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-18995" />
                    </div>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}